import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Slider } from '@/components/ui/slider';
import { Sprout, Leaf, Repeat, Sparkles, Ruler, Home, Clock, Check, Loader2, AlertTriangle, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Month-by-month supply ribbon from the wave harvest dates (gaps flagged when a
// sowing interval is wide enough to leave a month uncovered).
function harvestRibbon(waves) {
  const idxs = waves.map((w) => { const d = new Date(w.harvest_date); return d.getFullYear() * 12 + d.getMonth(); }).sort((a, b) => a - b);
  if (!idxs.length) return { cells: [], covered: 0 };
  const cov = new Set();
  for (let i = 0; i < idxs.length; i++) { cov.add(idxs[i]); if (i > 0 && idxs[i] - idxs[i - 1] <= 2) for (let m = idxs[i - 1]; m <= idxs[i]; m++) cov.add(m); }
  const first = idxs[0], last = idxs[idxs.length - 1];
  const cells = [];
  for (let m = first; m <= last; m++) cells.push({ idx: m, label: MONTHS_ABBR[((m % 12) + 12) % 12], covered: cov.has(m) });
  return { cells, covered: cells.filter((c) => c.covered).length };
}

// Unified wave visuals (shared metaphor with mobile): Mature → Seedling → Sprout.
const STAGE = {
  mature: { Icon: Leaf, color: '#1A4331', ring: 'rgba(26,67,49,0.14)' },
  seedling: { Icon: Sprout, color: '#6B9F4A', ring: 'rgba(107,159,74,0.16)' },
  sprout: { Icon: Sprout, color: '#C9A85B', ring: 'rgba(201,168,91,0.18)' },
};

export default function ContinuousHarvestPanel({ cropId, cropName, plantingId, initialAiOn }) {
  const [aiOn, setAiOn] = useState(initialAiOn !== false);
  const [duration, setDuration] = useState(4);
  const [interval, setIntervalWeeks] = useState(3);
  const [plan, setPlan] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [busyWave, setBusyWave] = useState(null);
  const timer = useRef(null);

  const recompute = useCallback(async (explain) => {
    const body = { crop_id: cropId, mode: aiOn ? 'auto' : 'manual', duration_months: duration, explain };
    if (!aiOn) body.interval_weeks = interval;
    if (explain) setThinking(true);
    try {
      const r = await axios.post(`${API}/succession/compute`, body, { withCredentials: true });
      setPlan(r.data);
      if (aiOn && r.data.interval_weeks) setIntervalWeeks(r.data.interval_weeks);
    } catch (e) { /* keep last */ }
    finally { if (explain) setThinking(false); }
  }, [cropId, aiOn, duration, interval]);

  // Load any saved plan first, then a fresh preview.
  useEffect(() => {
    let live = true;
    axios.get(`${API}/succession/plan/${plantingId}`, { withCredentials: true })
      .then((r) => {
        if (!live || !r.data.saved) return;
        setSaved(true);
        setAiOn(r.data.mode === 'auto');
        setDuration(r.data.duration_months || 4);
        setIntervalWeeks(r.data.interval_weeks || 3);
        setPlan(r.data);
      })
      .catch(() => {})
      .finally(() => { if (live) recompute(true); });
    return () => { live = false; };
  }, [plantingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced live preview as sliders move.
  const nudge = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => recompute(false), 240);
  };
  const commit = () => { clearTimeout(timer.current); recompute(true); };

  const toggleAi = async () => {
    const next = !aiOn;
    setAiOn(next);
    axios.patch(`${API}/users/me`, { intelligence_enabled: next }, { withCredentials: true }).catch(() => {});
    setTimeout(() => recompute(true), 60);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { planting_id: plantingId, crop_id: cropId, mode: aiOn ? 'auto' : 'manual', duration_months: duration };
      if (!aiOn) body.interval_weeks = interval;
      const r = await axios.post(`${API}/succession/plan`, body, { withCredentials: true });
      setPlan(r.data); setSaved(true);
    } catch (e) { /* noop */ }
    finally { setSaving(false); }
  };

  const plantWave = async (idx) => {
    setBusyWave(idx);
    try {
      if (!saved) { await save(); }
      const env = plan?.guardrail?.suggest_indoor ? 'greenhouse' : 'outdoor';
      await axios.post(`${API}/succession/plan/${plantingId}/waves/${idx}/plant`, { environment: env }, { withCredentials: true });
      const r = await axios.get(`${API}/succession/plan/${plantingId}`, { withCredentials: true });
      if (r.data.saved) setPlan(r.data);
    } catch (e) { /* noop */ }
    finally { setBusyWave(null); }
  };

  if (plan && plan.succession_suitable === false) return null;
  const waves = plan?.waves || [];
  const g = plan?.guardrail;

  return (
    <div className="mt-2 rounded-2xl p-4 border border-white/40 shadow-sm"
      style={{ background: 'linear-gradient(160deg, rgba(255,255,255,0.72), rgba(240,242,235,0.62))', backdropFilter: 'blur(14px)' }}
      data-testid={`ch-panel-${plantingId}`}>
      {/* Header + Auto-Pilot toggle */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5">
          <Repeat className="w-4 h-4 text-[#C9A85B]" />
          <span className="text-sm font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Continuous Harvest</span>
        </div>
        <button onClick={toggleAi} data-testid="ch-ai-toggle"
          className="flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors"
          style={{ background: aiOn ? '#1A4331' : '#E7E9E0', color: aiOn ? '#B4D330' : '#7A6B57' }}>
          {aiOn ? <Sparkles className="w-3 h-3" /> : <Ruler className="w-3 h-3" />}
          {aiOn ? 'AI Auto-Pilot' : 'Manual'}
        </button>
      </div>

      {/* Supply Duration slider */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold text-[#7A6B57] uppercase tracking-wide">Supply duration</span>
          <span className="text-xs font-bold text-[#1A4331]" data-testid="ch-duration-value">{duration} months</span>
        </div>
        <Slider min={2} max={12} step={1} value={[duration]} data-testid="ch-duration-slider"
          onValueChange={(v) => { setDuration(v[0]); nudge(); }} onValueCommit={commit} />
      </div>

      {/* Sowing Interval slider — only in Manual; smoothly collapses under AI */}
      <div style={{ maxHeight: aiOn ? 0 : 80, opacity: aiOn ? 0 : 1, overflow: 'hidden', transition: 'max-height 320ms ease, opacity 260ms ease' }}>
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-[#7A6B57] uppercase tracking-wide">Sow every</span>
            <span className="text-xs font-bold text-[#1A4331]" data-testid="ch-interval-value">{interval} week{interval === 1 ? '' : 's'}</span>
          </div>
          <Slider min={1} max={8} step={1} value={[interval]} data-testid="ch-interval-slider"
            onValueChange={(v) => { setIntervalWeeks(v[0]); nudge(); }} onValueCommit={commit} disabled={aiOn} />
        </div>
      </div>

      {aiOn && (
        <div className="flex items-center gap-1.5 mb-3 text-[11px] font-semibold text-[#1A4331] bg-[#6B9F4A]/12 px-2.5 py-1.5 rounded-lg w-fit" data-testid="ch-ai-interval">
          <Sparkles className="w-3 h-3 text-[#6B9F4A]" /> AI picks: sow every {plan?.interval_weeks ?? interval} weeks
        </div>
      )}

      {/* Wave rail */}
      {waves.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid={`ch-wave-rail-${plantingId}`}>
          {waves.map((w) => {
            const st = STAGE[w.stage] || STAGE.seedling;
            const planted = w.status === 'planted';
            const label = planted ? 'Planted' : (w.plant_in_days <= 0 ? 'Sow now' : `Plant in ${w.plant_in_days}d`);
            return (
              <div key={w.index} data-testid={`ch-wave-${w.index}`}
                className="flex-shrink-0 w-[92px] rounded-xl p-2.5 text-center border"
                style={{ background: planted ? 'rgba(107,159,74,0.14)' : '#FFFFFFAA', borderColor: st.ring }}>
                <div className="mx-auto mb-1 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: st.ring }}>
                  <st.Icon className="w-4.5 h-4.5" style={{ color: st.color }} strokeWidth={2.2} />
                </div>
                <div className="text-[10px] font-bold text-[#1A4331] leading-tight">{w.month_label}</div>
                <div className="text-[10px] text-[#7A6B57] leading-tight mb-1.5">{label}</div>
                {planted ? (
                  <div className="text-[10px] font-bold text-[#6B9F4A] flex items-center justify-center gap-0.5"><Check className="w-3 h-3" /> Done</div>
                ) : (
                  <button onClick={() => plantWave(w.index)} disabled={busyWave === w.index} data-testid={`ch-wave-plant-${w.index}`}
                    className="w-full text-[10px] font-bold text-white rounded-full py-1 flex items-center justify-center gap-0.5"
                    style={{ background: '#1A4331' }}>
                    {busyWave === w.index ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sprout className="w-3 h-3" /> Plant</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[11px] text-[#7A6B57]" data-testid={`ch-no-waves-${plantingId}`}>No outdoor waves fit right now — see the note below.</p>
      )}

      {/* Harvest supply timeline */}
      {waves.length > 1 && (() => {
        const rib = harvestRibbon(waves);
        if (!rib.cells.length) return null;
        return (
          <div className="mt-3" data-testid={`ch-timeline-${plantingId}`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Calendar className="w-3.5 h-3.5 text-[#6B9F4A]" />
              <span className="text-[11px] font-bold text-[#1A4331]">Harvest supply · {rib.covered} month{rib.covered === 1 ? '' : 's'}</span>
            </div>
            <div className="flex gap-1 overflow-x-auto pb-1">
              {rib.cells.map((c) => (
                <div key={c.idx} data-testid={`ch-timeline-cell-${c.idx}`}
                  className="flex-shrink-0 rounded-md text-center"
                  style={{ minWidth: 34, padding: '4px 2px', background: c.covered ? 'rgba(107,159,74,0.9)' : 'rgba(142,170,151,0.18)' }}>
                  <span className="text-[9px] font-bold" style={{ color: c.covered ? '#fff' : '#8EAA97' }}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Climate guardrail */}
      {g && g.triggered && (
        <div className="mt-3 rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(198,107,74,0.10)', border: '1px solid rgba(198,107,74,0.25)' }} data-testid="ch-guardrail">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#C66B4A]" />
          <div className="flex-1">
            <p className="text-[11px] text-[#5C3A2E] leading-snug font-medium">{g.message}</p>
            {g.suggest_indoor && (
              <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-[#1A4331] bg-[#C9A85B]/20 px-2 py-1 rounded-full" data-testid="ch-guardrail-indoor">
                <Home className="w-3 h-3" /> {g.kind === 'out_of_season' ? 'Waves plant as Greenhouse' : 'Later waves plant as Greenhouse'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Strategic why */}
      {plan?.why && (
        <p className="mt-3 text-[11px] text-[#1A4331] leading-snug flex items-start gap-1.5" data-testid="ch-why">
          {thinking ? <Loader2 className="w-3 h-3 mt-0.5 animate-spin text-[#6B9F4A]" /> : (aiOn ? <Sparkles className="w-3 h-3 mt-0.5 text-[#6B9F4A]" /> : <Clock className="w-3 h-3 mt-0.5 text-[#C9A85B]" />)}
          <span>{plan.why}</span>
        </p>
      )}

      {/* Save */}
      <button onClick={save} disabled={saving} data-testid="ch-save"
        className="mt-3 w-full rounded-full py-2 text-xs font-bold text-white flex items-center justify-center gap-1.5"
        style={{ background: 'linear-gradient(135deg, #1A4331, #2C6E49)' }}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        {saved ? 'Update plan' : 'Save this plan'}
      </button>
    </div>
  );
}
